<script setup lang="ts">
import { guest, IconCheck, IconLock, userInjectionKey } from '#shared'
import { computed, inject, onMounted, ref, unref, useTemplateRef } from 'vue'
import { useChatId } from '#entities/chat'
import { useChatAuthMutation } from '../state/use-chat-auth-mutation'

const inputType = ref('text')
const input = useTemplateRef('input')

const password = ref('')
const isConsented = ref(false)
const chatId = useChatId()

onMounted(() => {
  input.value?.focus()

  setTimeout(() => {
    inputType.value = 'password'
  })
})

const currentUser = inject(userInjectionKey)
const isLoggedIn = computed(() => {
  const currentUserVal = unref(currentUser)
  return !!currentUserVal && currentUserVal !== guest
})

const label = computed(() => {
  const prefix = 'This is protected chat.'
  return isLoggedIn.value
    ? `${prefix} You have to enter password to access it.`
    : `${prefix} You have to log in and enter password to access it.`
})

const loginUrl = import.meta.env.VITE_LOGIN_URL

const chatAuthMutation = useChatAuthMutation(chatId)

const canSubmit = computed(() => isConsented.value && !!password.value && !chatAuthMutation.isPending.value)

const onFormSubmit = () => {
  chatAuthMutation.mutate(password.value)
}
</script>

<template>
  <div :class="$style.root">
    <IconLock :class="$style.icon" />

    <div :class="$style.label">{{ label }}</div>

    <form v-if="isLoggedIn" :class="$style.form" id="chat_access_form" @submit.prevent="onFormSubmit">
      <div :class="$style.formRow">
        <input
          :class="$style.password"
          ref="input"
          :type="inputType"
          placeholder="Chat password"
          name="chat_protection_key"
          id="chat_protection_key"
          autocomplete="new-password"
          :key="chatId"
          v-model="password"
        />
        <button :class="$style.submit" name="enter_chat" id="enter_chat" :disabled="!canSubmit">Enter chat</button>
      </div>

      <div :class="$style.formRow">
        <div :class="$style.disclosure">
          <input :class="$style.disclosureCheckbox" type="checkbox" id="no_disclose" v-model="isConsented" />
          <label :class="$style.disclosureLabel" for="no_disclose">
            <div :class="$style.disclosureCheckboxView">
              <IconCheck :class="$style.disclosureCheckboxIcon" />
            </div>
            <div :class="$style.disclosureText">I will not disclose content of this chat</div>
          </label>
        </div>
      </div>

      <div :class="$style.formRow" v-if="chatAuthMutation.isError.value">
        <div :class="$style.error">Invalid password</div>
      </div>
    </form>

    <a v-else :class="$style.login" :href="loginUrl"> Log in </a>
  </div>
</template>

<style lang="scss" module>
@use '#mixins/media' as media;
@use '#mixins/button' as button;

.root {
  height: 100%;
  display: flex;
  padding: 20px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.icon {
  width: 64px;
  height: 64px;
  margin-bottom: 32px;
}

.label {
  font-size: 20px;
  font-weight: 500;
  color: #7a7a7a;
  margin-bottom: 64px;
  text-align: center;
}

.form {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  max-width: 600px;
  gap: 20px;
}

.formRow {
  display: flex;
  gap: 10px;
}

.password {
  outline: none;
  resize: none;
  background: #2f303c;
  font-family: 'HelveticaNeueSbbChat', 'Noto Color Emoji', system-ui, Avenir, Helvetica, Arial, sans-serif;
  border: none;
  line-height: 1.3;
  min-width: 0;
  margin: 0;
  min-height: fit-content;
  width: 0;
  flex-grow: 1;
  box-sizing: content-box;
  color: white !important;
  padding: 12px 15px;
  font-size: 16px;
  border: 2px solid #474756;
  border-radius: 4px;

  @include media.mobile {
    padding: 7px 10px;
    font-size: 14px;
    max-height: 69px;
  }
}

.submit {
  @include button.primary;
  height: 49px;
}

.disclosure {
  display: flex;
  align-items: center;
}

.disclosureLabel {
  display: flex;
}

.disclosureCheckboxView {
  width: 16px;
  height: 16px;
  margin-right: 8px;
  border-radius: 2px;
  border: 1px solid #474756;
  display: flex;
  align-items: center;
  justify-content: center;

  .disclosureCheckbox:checked ~ * > & {
    background-color: #397ae9;
    border-color: #397ae9;
  }
}

.disclosureCheckboxIcon {
  width: 12px;
  height: 12px;
  opacity: 0;
  color: white;

  .disclosureCheckbox:checked ~ * & {
    opacity: 1;
  }
}

.error {
  color: var(--sc-error);
}

.login {
  @include button.primary;
}
</style>
